/**
 * @description Prepares a list of navigation links based on the user's settings
 */

import React from 'react';

import { UserSettingsContext } from '../contexts/userSettings';

/**
 * Simple function that sorts an array of links alphabetically by their label
 * @param {{ label: string }[]} arr - an array to be sorted
 * @returns {{ label: string }[]} - sorted array
 */
function sortLinks (arr) {
  return arr.sort((navA, navB) => navA.label.localeCompare(navB.label, 'en', { sensitivity: 'base' }));
}

const useNavigationLinks = () => {
  // Retrieve the user settings
  const userSettings = React.useContext(UserSettingsContext);

  // Prepare a list of navigation links based on teh user settings defined
  const navigationLinks = React.useMemo(() => {
    const list = [];

    // If no user settings are defined yet, renbder nothing
    if (!userSettings) {
      return list;
    }

    // Build a list of links based on the main navigation in the user's settings
    const mainNavigation = userSettings.mainNavigation ?? [];
    const mainNavigationLinks = mainNavigation.map((entry) => ({
      label: entry.name,
      path: `/${ entry.path }`,
      reloadDocument: true
    }));

    // Add those links to the list
    list.push(...mainNavigationLinks);

    // If the user has visit navigation...
    if (userSettings.visitsNavigation?.length) {
      // Build a list of links based on the visit navigation in the user's settings
      const visitLinks = sortLinks(
        userSettings.visitsNavigation.map((entry) => ({
          label: entry.name,
          path: `/${ entry.path }`,
          reloadDocument: true
        }))
      );

      // Add those links as a dropdown to the list
      list.push({
        entries: visitLinks,
        isDropdown: true,
        label: 'Visits'
      });
    }

    // If the user has classified materials navigation
    if (userSettings.classifiedMaterialsNavigation?.length) {
      // Build a list of links based on the classified materials navigation in the user's settings
      const classifiedLinks = sortLinks(
        userSettings.classifiedMaterialsNavigation.map((entry) => ({
          label: entry.name,
          path: `/${ entry.path }`,
          reloadDocument: true
        }))
      );

      // Add those links as a dropdown to the list
      list.push({
        entries: classifiedLinks,
        isDropdown: true,
        label: 'Classified Materials'
      });
    }

    // Sort the list of links based on their label alphabetically and return that sorted list
    return sortLinks(list);
  }, [userSettings]);

  return navigationLinks;
}

export default useNavigationLinks;
